import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Section from '../components/Section';
import SEO from '../components/SEO';
import content from '../data/content.json';

interface ExperienceItem {
  period: string;
  role: string;
  description?: string;
}

interface LawyerProfile {
  name: string;
  title: string;
  slug: string;
  practiceAreas?: string[];
  email?: string;
  phone?: string;
  officePhone?: string;
  bio?: string;
  experience?: ExperienceItem[];
}

const LawyerDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const lawyersSection = content.about.content.find(sec => sec.section === 'Lawyers');
  const profiles = lawyersSection?.profiles || [];

  const lawyer = profiles.find((prof: any) => prof.slug === slug) as LawyerProfile | undefined;

  if (!lawyer) {
    return <Navigate to="/om-oss" replace />;
  }

  const name = lawyer.name.replace(/^#+\s*/, '');
  const photoMap: { [key: string]: string } = {
    'Tove Kristin Farstad': '/privateImages/tove.webp',
    'Thorleif G. Bruland': '/privateImages/throleif.webp',
  };

  // Format bio paragraphs
  const bioParagraphs = lawyer.bio?.split('\n\n') || [];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": name,
    "jobTitle": lawyer.title,
    "email": lawyer.email,
    "telephone": lawyer.phone,
    "worksFor": {
      "@type": "LegalService",
      "name": "Advokatfirmaet Bruland og Farstad"
    }
  };

  return (
    <>
      <SEO
        title={`${name} - ${lawyer.title}`}
        description={bioParagraphs[0] || `Møt ${name}, ${lawyer.title} hos Advokatfirmaet Bruland og Farstad.`}
        keywords={`${name}, advokat Oslo, ${lawyer.practiceAreas?.join(', ')}`}
        canonicalUrl={`/om-oss/${slug}`}
        structuredData={structuredData}
      />

      {/* Page Header */}
      <Section
        title={name}
        subtitle={lawyer.title}
        background="gray"
        spacing="md"
        centered
      />

      {/* Main Content */}
      <Section background="white" spacing="lg">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Left Column - Photo & Contact */}
            <div className="md:col-span-1">
              {/* Photo */}
              {photoMap[name] ? (
                <img
                  src={photoMap[name]}
                  alt={`Portrett av ${name}`}
                  className="w-full rounded-lg mb-6 object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="w-full aspect-square rounded-lg mb-6 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <svg className="w-24 h-24 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}

              {/* Practice Areas */}
              {lawyer.practiceAreas && lawyer.practiceAreas.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif">Fagområder</h3>
                  <ul className="text-gray-700 space-y-2">
                    {lawyer.practiceAreas.map((area, idx) => (
                      <li key={idx}>{area}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Contact Info */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif">Kontakt</h3>
                <div className="space-y-2 text-gray-700">
                  {lawyer.email && (
                    <p>
                      <a href={`mailto:${lawyer.email}`} className="text-accent hover:underline">
                        {lawyer.email}
                      </a>
                    </p>
                  )}
                  {lawyer.phone && (
                    <p>
                      <a href={`tel:${lawyer.phone}`} className="text-accent hover:underline">
                        {lawyer.phone}
                      </a>
                    </p>
                  )}
                  {lawyer.officePhone && (
                    <p>
                      <a href={`tel:${lawyer.officePhone}`} className="text-accent hover:underline">
                        {lawyer.officePhone}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Bio & Experience */}
            <div className="md:col-span-2">
              {/* Biography */}
              {bioParagraphs.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 font-serif">Om {name.split(' ')[0]}</h2>
                  <div className="prose prose-lg max-w-none">
                    {bioParagraphs.map((paragraph, idx) => (
                      <p key={idx} className="text-gray-700 leading-relaxed mb-4" style={{ whiteSpace: 'pre-line' }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Experience Timeline */}
              {lawyer.experience && lawyer.experience.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 font-serif">Utdanning og erfaring</h2>
                  <div className="space-y-6">
                    {lawyer.experience.map((exp, idx) => (
                      <div key={idx} className="border-l-4 border-accent pl-6 pb-4">
                        <p className="text-accent font-semibold mb-1">{exp.period}</p>
                        <p className="text-gray-900 font-medium text-lg">{exp.role}</p>
                        {exp.description && (
                          <p className="text-gray-600 mt-1">{exp.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default LawyerDetail;
